import React from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MicIcon from '@mui/icons-material/Mic';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { getimgticket } from '../../../../../services/supports/supports';
import { showAlert } from '../../../../../main_components/alerts/alert_response';

interface AttachedFileListProps {
    fileNames: string[];
    docsData: any[];
    ticketId: number;
    setDocsData: (data: any) => void;
    setPreviewFile: (file: { url: string; type: string } | null) => void;
    notificationSystemRef: React.MutableRefObject<any>;
}

const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png'].includes(ext || '')) return <ImageIcon sx={{ color: '#8B5CF6' }} />;
    if (ext === 'pdf') return <PictureAsPdfIcon sx={{ color: '#EF4444' }} />;
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'wma', 'flac', 'opus'].includes(ext || ''))
        return <MicIcon sx={{ color: '#10B981' }} />;
    return <InsertDriveFileIcon sx={{ color: '#6B7280' }} />;
};

const getMimeType = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const map: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        pdf: 'application/pdf',
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
        m4a: 'audio/mp4',
    };
    return map[ext || ''] || 'application/octet-stream';
};

const AttachedFileList: React.FC<AttachedFileListProps> = ({
    fileNames,
    docsData,
    ticketId,
    setDocsData,
    setPreviewFile,
    notificationSystemRef,
}) => {
    const handleDownloadAndPreview = async (fileName: string) => {
        try {
            const res = await getimgticket(ticketId, fileName);
            const mimeType = getMimeType(fileName);
            const fileBase64 = res.data.file;

            const newFile = {
                file_name: fileName,
                file: fileBase64,
            };

            setDocsData((prev: any) => [...(prev || []), newFile]);

            if (mimeType.startsWith('image/') || mimeType === 'application/pdf') {
                setPreviewFile({
                    url: `data:${mimeType};base64,${fileBase64}`,
                    type: mimeType,
                });
            }
        } catch (err) {
            showAlert({
                msj: `Error al descargar el archivo ${fileName}. Inténtalo de nuevo más tarde.`,
                status: false,
                notificationSystemRef,
            });
        }
    };

    return (
        <Box mt={1} p={2} sx={{ bgcolor: '#FDF4FF', borderRadius: 2 }}>
            {fileNames.map((fileName, idx) => {
                const found = docsData?.find((doc: any) => doc.file_name === fileName);
                const mimeType = getMimeType(fileName);
                const base64Url = found ? `data:${mimeType};base64,${found.file}` : null;
                const isPreviewable = mimeType.startsWith('image/') || mimeType === 'application/pdf';
                const isAudio = mimeType.startsWith('audio/');

                return (
                    <Box
                        key={idx}
                        sx={{
                            bgcolor: '#fff',
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            mb: 1,
                            boxShadow: 1,
                        }}
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={1}>
                                {getFileIcon(fileName)}
                                <Typography fontSize={13} noWrap maxWidth="160px">
                                    {fileName}
                                </Typography>
                            </Box>

                            {found && isPreviewable && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <IconButton size="small" onClick={() => setPreviewFile({ url: base64Url!, type: mimeType })}>
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = base64Url!;
                                            link.download = fileName;
                                            link.click();
                                        }}
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}

                            {found && !isPreviewable && !isAudio && (
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = base64Url!;
                                        link.download = fileName;
                                        link.click();
                                    }}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            )}

                            {!found && (
                                <Button size="small" variant="outlined" onClick={() => handleDownloadAndPreview(fileName)}>
                                    <DownloadIcon fontSize="small" />
                                </Button>
                            )}
                        </Box>

                        {found && isAudio && (
                            <audio controls src={base64Url ?? undefined} style={{ width: '100%', marginTop: 4, height: 28 }} />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default AttachedFileList;
